import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { Device } from '../../types/models';
import { getDevicePrices } from '../../services/devices';
import { estimateBatteryLife } from '../../lib/batteryLife';
import { DeviceThumb } from './DeviceThumb';
import { StatusPill } from './StatusPill';

interface ProductCardProps {
  device: Device;
  batteryHealthPct?: number;
  purchasedAt?: string;
  nickname?: string;
  showPrice?: boolean;
  className?: string;
}

function formatPrice(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat('de-AT', { style: 'currency', currency }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

export function ProductCard({
  device,
  batteryHealthPct,
  purchasedAt,
  nickname,
  showPrice = true,
  className = '',
}: ProductCardProps) {
  const pricesQ = useQuery({
    queryKey: ['device', device._id, 'prices'],
    queryFn: () => getDevicePrices(device._id),
    enabled: showPrice,
  });

  const cheapest = pricesQ.data
    ? pricesQ.data.slice().sort((a, b) => a.priceCents - b.priceCents)[0]
    : undefined;

  const estimate =
    typeof batteryHealthPct === 'number' ? estimateBatteryLife(batteryHealthPct, purchasedAt) : undefined;

  return (
    <Link
      to={`/devices/${device._id}`}
      className={`card flex flex-col gap-3 hover:border-lime-500 transition-colors ${className}`}
    >
      <DeviceThumb imageUrl={device.imageUrl} alt={device.name} className="aspect-video" />
      <div className="flex-1">
        <div className="text-xs uppercase tracking-wide text-neutral-400">{device.brand}</div>
        <div className="text-lg font-semibold text-neutral-100">{nickname || device.name}</div>
      </div>
      {estimate && <StatusPill tone={estimate.tone} label={estimate.label} />}
      {cheapest && (
        <div className="text-sm font-data font-semibold text-lime-400">
          From {formatPrice(cheapest.priceCents, cheapest.currency)}
        </div>
      )}
    </Link>
  );
}
